import { PropsWithChildren, useEffect, useState } from 'react';
import { useStyles } from './ReceiptScreen.styles';
import { useGlobalDispatch, useGlobalState } from '../../common/context/AppContext';
import { convertDkDateToIsoDate, customerIdListToArray, GridWrapper } from '@flos/react-components';
import {
  BRAND,
  EMAIL_EVENT,
  EMAIL_IDENTIFIER,
  IBuyInsurance,
  ISalesCompleteEmail,
  PREMIUMRATE,
  PRODUCTKEY,
  TERMINATION_WHEN
} from '../../common/interface/IPayload';
import { IAppProps } from '../../common/interface/IAppProvider';
import { useHousehold } from '../../common/customHooks/useHousehold';
import { buyInsurance, doEboksSubscription, sendEmail } from '../../common/async/AsyncCalls';
import track, { ecommerce, errorTypes, product } from 'c9-tracking';
import { ReceiptText } from './ReceiptText';
import { ReceiptForm } from './ReceiptForm';

interface IState {
  loading: boolean;
}

export default function ReceiptScreenView(props: PropsWithChildren<IAppProps>): JSX.Element {
  const {
    householdSize,
    aboutYouData,
    selectedPackage,
    hasEboksSubscription,
    salesComplete,
    selectedPaymentOption,
    effectiveDate,
    terminationWhen,
    bankInfo,
    terminationRequested,
    terminationCompany,
    ecommerceProduct,
    cprNo,
    isLoading
  } = useGlobalState();
  const appDispatch = useGlobalDispatch();
  const [state, setState] = useState<IState>({ loading: false });
  const classes = useStyles();
  const packageName = selectedPackage.packageName;
  const { state: householdSizeGTI } = useHousehold(props.config);

  useEffect(() => {
    const canSendEmail = sessionStorage.getItem('onsa-travel-can-send-email');

    if (
      !state.loading &&
      householdSizeGTI.progress === 'done-ok' &&
      !salesComplete &&
      selectedPaymentOption &&
      canSendEmail === 'true'
    ) {
      sessionStorage.removeItem('onsa-travel-can-send-email');
      setState((prevState) => ({ ...prevState, loading: true }));

      const customerId = customerIdListToArray(window.topContext.user.getActiveUser().customerIdList)[0];
      const rate = selectedPaymentOption.type === 'Månedligt' ? PREMIUMRATE.MONTHLY : PREMIUMRATE.YEARLY;
      const buyInsurancePayload: IBuyInsurance = {
        brand: BRAND.TOPDANMARK,
        emailAddress: aboutYouData.email,
        premiumRate: rate,
        productId: selectedPackage.productId,
        productKey: PRODUCTKEY.REJSE,
        quoteId: selectedPackage.quoteId,
        packageName: packageName,
        eboks: hasEboksSubscription,
        accept: !terminationRequested,
        kvhxKey: aboutYouData?.kvhx
      };

      const salesCompletedPayload: ISalesCompleteEmail = {
        identifier: EMAIL_IDENTIFIER.TRAVEL_SALES_COMPLETE,
        event: EMAIL_EVENT.SALES_COMPLETE,
        customerId,
        cprNumber: cprNo.value,
        emailAddress: aboutYouData.email,
        fullName: aboutYouData.name,
        phoneNumber: aboutYouData.phone.replace(/\s/g, ''), //dont need the space
        premiumRateDescription: selectedPaymentOption.type,
        quoteId: selectedPackage.quoteId,
        residentialAddress: aboutYouData.addr,
        effectiveDate: effectiveDate
          ? effectiveDate
          : terminationWhen === TERMINATION_WHEN.QUICK
            ? 'Hurtigst mulig'
            : 'Til forfald',
        packageName: packageName,
        eboks: hasEboksSubscription,
        householdSize: +householdSize
      };

      if (bankInfo.accNumber && bankInfo.regNumber) {
        salesCompletedPayload.bankAccountNumber = bankInfo.accNumber;
        salesCompletedPayload.bankRegistrationNumber = bankInfo.regNumber;
        buyInsurancePayload.bankAccountNumber = bankInfo.accNumber;
        buyInsurancePayload.bankRegistrationNumber = bankInfo.regNumber;
      }

      if (terminationRequested) {
        salesCompletedPayload.currentInsuranceCompany = terminationCompany;
      } else {
        buyInsurancePayload.effectiveDate = convertDkDateToIsoDate(effectiveDate as string);
      }

      if (hasEboksSubscription) {
        doEboksSubscription(props, appDispatch, customerId)
          .then(() => {
            props.c9log.info('eboks subscription is success');
            track.info('eboks subscription is success');
          })
          .catch((error) => {
            props.c9log.error(`eboks subscription is failed: ${error}`, 'ReceiptScreen');
            track.fault(`eboks subscription is failed ${error}`, errorTypes.tech, 'GTEBoks subscription');
          });
      }
      props.c9log.debug(`Calling buyInsurance ${JSON.stringify(buyInsurancePayload)}`, 'ReceiptScreen');
      props.c9log.debug(
        `The accept flag was: ${buyInsurancePayload.accept}, terminationRequested: ${terminationRequested}, householdSize: ${householdSize}, householdSizeGTI.value: ${householdSizeGTI.value}`,
        'ReceiptScreen'
      );

      buyInsurance(props, appDispatch, buyInsurancePayload)
        .then((resp) => {
          if (resp) {
            const regex = /^Duplicate offer exists/;
            const message = (resp.message as string) || '';
            const dup = regex.test(message);

            if (!(!resp.canProceed && dup)) {
              props.c9log.debug(`Calling sendEmail ${JSON.stringify(salesCompletedPayload)}`, 'ReceiptScreen');
              if (resp.insuranceActivated) {
                salesCompletedPayload.event = EMAIL_EVENT.INSURANCE_ACTIVATED;
              }

              sendEmail(props, appDispatch, salesCompletedPayload).then(() => {
                ecommerce.purchase([ecommerceProduct as product], buyInsurancePayload.quoteId);
                appDispatch({ salesComplete: true, stateChange: true });
              });
            }
          }
        })
        .catch((err) => {
          props.c9log.error(`Buy endpoint failed. error: ${err}`, 'ReceiptScreen');
          track.fault(`Buy endpoint failed. ${err}`, errorTypes.tech, 'ReceiptScreen');
        });
    }
  }, [
    state.loading,
    props,
    appDispatch,
    packageName,
    salesComplete,
    selectedPaymentOption,
    aboutYouData.email,
    aboutYouData.name,
    aboutYouData.phone,
    aboutYouData.addr,
    aboutYouData.kvhx,
    selectedPackage.productId,
    selectedPackage.quoteId,
    hasEboksSubscription,
    cprNo,
    effectiveDate,
    bankInfo.accNumber,
    bankInfo.regNumber,
    terminationRequested,
    terminationCompany,
    householdSize,
    householdSizeGTI.progress,
    householdSizeGTI.value,
    ecommerceProduct,
    terminationWhen
  ]);

  if (!isLoading && salesComplete) {
    return (
      <div className={classes.wrapper}>
        <GridWrapper>
          <ReceiptText config={props.config} />
          <ReceiptForm config={props.config} />
        </GridWrapper>
      </div>
    );
  } else {
    return <></>;
  }
}
